import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import { mask as maskFunction, unMask } from 'remask';

import Input from '@/components/global/Inputs/Input';
import Rastreamento from '@/components/Pages/Rastreamento/';
import { useRastreamento } from '@/components/Pages/Rastreamento/Provider';

import { Container } from './styles';

import { validateCPForCNPJ } from '@/utils/cpfOrCnpjValidator/cpfAndCnpjValidator';
import removeAllNonNumberCharacters from '@/utils/removeAllNonNumberCharacters';
import { RASTREAMENTO_URL } from '@/config';

const Form = (): JSX.Element => {
  const { documentType } = useRastreamento();
  const router = useRouter();

  const maskValue = useCallback((str: string | string[], mask: string[]) => {
    const originalValue: string = unMask(str);

    if (mask) {
      return maskFunction(originalValue, mask);
    }

    return originalValue;
  }, []);

  const initialValues = {
    cpfOrCnpj:
      (router.query.cnpj &&
        maskValue(router.query.cnpj, ['99.999.999/9999-99'])) ||
      (router.query.cpf && maskValue(router.query.cpf, ['999.999.999-99'])) ||
      '',
    type: router.query.tipo || '',
    nf: router.query.notaFiscal || '',
  };

  const validationSchema = yup.object().shape({
    cpfOrCnpj: yup
      .string()
      .required('Entre com um CPF ou CNPJ')
      .test('test-cnpj-or-cpf', 'CPF ou CNPJ inválido!', (cpfOrCnpj) =>
        validateCPForCNPJ(cpfOrCnpj)
      ),
    type:
      documentType === 'cnpj'
        ? yup.string().required('Escolha um tipo')
        : yup.string(),
    nf:
      documentType === 'cnpj'
        ? yup.string().required('Entre com uma nota fiscal')
        : yup.string(),
  });

  const onSubmit = useCallback((data) => {
    const documentNumber = removeAllNonNumberCharacters(data.cpfOrCnpj);
    const url = new URL(RASTREAMENTO_URL);
    url.searchParams.set('document', documentNumber);

    if (documentNumber.length <= 11) {
      url.searchParams.set('cpf', documentNumber);
    } else {
      url.searchParams.set('cnpj', documentNumber);
      if (data.type) {
        url.searchParams.set('tipo', data.type);
      }
      if (data.nf) {
        url.searchParams.set('notaFiscal', data.nf);
      }
    }

    window.location.href = url.toString();
  }, []);

  return (
    <Rastreamento>
      <Container>
        <Formik
          onSubmit={onSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnBlur={false}
          enableReinitialize
        >
          {({ values }) => (
            <FormikForm>
              <Input
                name="cpfOrCnpj"
                mask={['999.999.999-99', '99.999.999/9999-99']}
                type="tel"
                placeholder="Digite um CPF ou CNPJ"
              />
              {removeAllNonNumberCharacters(values.cpfOrCnpj as string).length >
                11 && (
                <>
                  <div className="radio cnpj">
                    <h3>Tipo do CNPJ</h3>
                    <Field
                      type="radio"
                      name="type"
                      value="remetente"
                      id="remetente"
                    />
                    <label htmlFor="remetente">Remetente</label>
                    <Field
                      type="radio"
                      name="type"
                      value="destinatario"
                      id="destinatario"
                    />
                    <label htmlFor="destinatario">Destinatário</label>
                  </div>
                  <ErrorMessage name="type" component="span" />
                  <Input
                    name="nf"
                    placeholder="Nº da nota fiscal"
                    type="tel"
                    className="cnpj"
                  />
                </>
              )}

              <button type="submit">Buscar</button>
            </FormikForm>
          )}
        </Formik>
      </Container>
    </Rastreamento>
  );
};

export default Form;
